import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import React, { useContext, useState, useEffect } from "react";
import { useQueryClient } from "react-query";
import { ToastContainer } from "react-toastify";
import { ApiDataContext } from "../../../contexts/ApiDataContext";
import useDialog from "../../../hooks/dialog/useDialog";
import CustomAutoComplete from "../../shared/autoCompletes/CustomAutoComplete";
import CustomLoadingButton from "../../shared/button/CustomLoadingButton";
import CustomDialog from "../../shared/dialog/CustomDialog";
import useAddressForm from "../../shared/address/controller";
import useEnginnerForm from "./controller";
import EngineerForm from "./EngineerForm";
import { deleteEngineer, updateEngineer } from "../../../services/api/api";
import { useLoadingToast } from "../../../hooks/toast/toasts";

export default function EditEngineer({ initialEngineerId, onEngineerIdLoad, onSuccess }) {
  const controller = useEnginnerForm();
  const addressController = useAddressForm();
  const { apiData } = useContext(ApiDataContext);
  const [engineerValue, setEngineerValue] = useState("");
  const [requestLoading, setRequestLoading] = useState(false);
  const [deleteRequestLoading, setDeleteRequestLoading] = useState(false);
  const { initializeLoadingToast, updateLoadingToast } = useLoadingToast();

  // Carregar engenheiro automaticamente quando initialEngineerId for fornecido
  useEffect(() => {
    if (initialEngineerId && apiData.engineers) {
      const engineer = apiData.engineers.find(e => e.id === initialEngineerId);
      if (engineer && engineer.id !== engineerValue?.id) {
        handleEngineerChange(null, engineer);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEngineerId, apiData.engineers]);

  const handleEngineerChange = async (event, newValue) => {
    setEngineerValue(null);
    setEngineerValue(newValue);

    controller.loadData(newValue);
    addressController.loadData(newValue);
  };

  // Wrapper para handleEngineerChange que também atualiza a URL
  const handleEngineerChangeWithUrl = async (event, newValue) => {
    await handleEngineerChange(event, newValue);
    if (newValue && newValue.id && onEngineerIdLoad) {
      onEngineerIdLoad(newValue.id);
    }
  };

  const engineer = {
    value: engineerValue,
    onChange: handleEngineerChangeWithUrl,
  };

  const queryClient = useQueryClient();

  const { open, handleClickOpen, handleClickClose } = useDialog();

  const handleUpdateClick = async () => {
    let updateToastId;
    try {
      if (!controller.areBasicFieldsValid()) {
        return;
      }
      setRequestLoading(true);
      updateToastId = initializeLoadingToast({
        text: `Atualizando Engenheiro...`,
      });

      const engineerData = {
        ...controller.values(),
        ...addressController.values(),
      };
      const { status } = await updateEngineer(controller.id, engineerData);

      if (status === 200) {
        queryClient.invalidateQueries({ queryKey: ["baseDataQuery"] });

        updateLoadingToast({
          id: updateToastId,
          text: `Engenheiro atualizado com sucesso`,
          type: "success",
        });

        // Chamar callback de sucesso após um delay para mostrar o toast
        if (onSuccess) {
          setTimeout(() => {
            onSuccess();
          }, 1000);
        }
      }
    } catch (error) {
      updateLoadingToast({
        id: updateToastId,
        text: error?.response?.data?.message ?? `Erro ao atualizar engenheiro, tente novamente`,
        type: "error",
      });
    } finally {
      setRequestLoading(false);
    }
  };

  const handleDeleteClick = async () => {
    let deleteToastId;
    try {
      setRequestLoading(true);

      deleteToastId = initializeLoadingToast({
        text: `Excluindo Engenheiro...`,
      });

      // Chama a função de deleção
      const response = await deleteEngineer(controller.id);
      const { status, data } = response;

      if (status === 200) {
        queryClient.invalidateQueries({ queryKey: ["baseDataQuery"] });
        controller.clearAllFields();
        addressController.clearAllFields();
        updateLoadingToast({
          id: deleteToastId,
          text: `Engenheiro Excluído com sucesso`,
          type: "success",
        });
        handleClickClose();
        setEngineerValue(null);

        // Chamar callback de sucesso após um delay
        if (onSuccess) {
          setTimeout(() => {
            onSuccess();
          }, 1000);
        }
      }
    } catch (error) {
      updateLoadingToast({
        id: deleteToastId,
        text: error.response?.data?.message ?? `Erro ao Excluir Engenheiro, tente novamente`,
        type: "error",
      });
    } finally {
      setRequestLoading(false);
    }
  };

  return (
    <Box
      sx={{
        mt: 2,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
      }}
    >
      <ToastContainer />

      <CustomDialog
        open={open}
        handleClickClose={handleClickClose}
        title="Excluir Engenheiro"
        contentText="As informações do engenheiro serão excluídas e não poderão ser recuperadas."
        requestLoading={deleteRequestLoading}
        handleConfirmationClick={handleDeleteClick}
        confirmationClickText="Excluir"
        requestLoadingText="Excluindo..."
      />

      {/* AutoComplete só aparece se não foi passado initialEngineerId (modo standalone) */}
      {!initialEngineerId && (
        <CustomAutoComplete
          params={engineer}
          options={apiData.engineers}
          disabled={false}
          label="Selecione o Engenheiro"
          width="40%"
        />
      )}

      {engineerValue ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            p: 2,
            justifyContent: "space-between",
            borderRadius: 3,
          }}
        >
          <EngineerForm layout="vertical" controller={controller} addressController={addressController} />

          <Box
            display="flex"
            width="45%"
            sx={{ justifyContent: "space-between", alignSelf: "flex-end", mt: 2 }}
          >
            <Button
              variant="outlined"
              color="error"
              sx={{
                width: "30%",
              }}
              disabled={requestLoading || deleteRequestLoading}
              onClick={handleClickOpen}
            >
              Excluir
            </Button>

            <CustomLoadingButton
              buttonText="Atualizar"
              buttonLoadingText="Atualizando..."
              sx={{ width: "35%" }}
              disabled={requestLoading || deleteRequestLoading}
              handleButtonClick={handleUpdateClick}
              requestLoading={requestLoading}
            />
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}
